export type GuideSection = {
  title: string
  paragraphs: string[]
  bullets?: string[]
}

export type GuideRelatedLink = {
  title: string
  href: string
  description?: string
}

export type GuideCategory =
  | "safety"
  | "fermentation"
  | "storage"
  | "basics"
  | "troubleshooting"

export type GuideArticle = {
  slug: string
  title: string
  description: string
  excerpt: string
  eyebrow: string
  readTime: string
  updatedAt: string
  category: GuideCategory
  intro: string[]
  sections: GuideSection[]
  relatedRecipes: GuideRelatedLink[]
  relatedGuides?: GuideRelatedLink[]
  relatedCalculators?: GuideRelatedLink[]
}

export const guides: GuideArticle[] = [
  {
    slug: "sterilizaciya-banok",
    title: "Як стерилізувати банки для консервації",
    description:
      "Пояснюємо, як правильно стерилізувати банки та кришки для домашньої консервації: способи, час, поширені помилки та практичні поради.",
    excerpt:
      "Простий і зрозумілий гайд про стерилізацію банок: у духовці, на парі, в киплячій воді та перед розливанням гарячих заготовок.",
    eyebrow: "Безпека заготовок",
    readTime: "5 хв читання",
    updatedAt: "2026-03-16",
    category: "safety",
    intro: [
      "Стерилізація банок — один із найважливіших етапів домашньої консервації. Саме чиста тара допомагає зменшити ризик псування заготовок і зробити зберігання спокійнішим та надійнішим.",
      "Спосіб стерилізації можна обрати залежно від того, що вам зручніше: духовка, пара, каструля з водою або швидке прогрівання перед гарячою заливкою. Головне — використовувати чисті банки без тріщин і пошкоджень.",
    ],
    sections: [
      {
        title: "Що підготувати перед стерилізацією",
        paragraphs: [
          "Перед початком уважно огляньте банки: на склі не повинно бути сколів, тріщин чи деформацій. Кришки також мають бути цілими, без іржі та пошкоджень.",
          "Банки потрібно добре вимити в гарячій воді з содою або м’яким мийним засобом, а потім ретельно сполоснути. Якщо на стінках залишаться сліди жиру чи пилу, стерилізація не дасть бажаного результату.",
        ],
        bullets: [
          "перевірте банки на сколи й тріщини",
          "добре вимийте скло й кришки",
          "використовуйте лише чисті рушники та поверхні",
        ],
      },
      {
        title: "Основні способи стерилізації",
        paragraphs: [
          "У духовці банки зручно стерилізувати одразу партією. Їх ставлять у холодну або ледь теплу духовку, після чого поступово нагрівають. Це зменшує ризик різкого перепаду температур.",
          "На парі часто стерилізують невелику кількість банок. Це простий домашній спосіб, якщо потрібно швидко підготувати тару для кількох рецептів.",
          "У киплячій воді стерилізація теж працює добре, але потребує трохи більше уваги. Банки слід ставити обережно, щоб уникнути ударів одна об одну.",
        ],
        bullets: [
          "духовка — для кількох банок одразу",
          "пара — для невеликих партій",
          "кипляча вода — класичний домашній спосіб",
        ],
      },
      {
        title: "Часті помилки",
        paragraphs: [
          "Найпоширеніша помилка — стерилізувати лише банки, але забувати про кришки. Для надійного результату чистими мають бути всі елементи, які контактують із заготовкою.",
          "Ще одна типова проблема — різкий перепад температур. Якщо в холодну банку одразу налити дуже гарячий маринад або сироп, скло може тріснути.",
        ],
        bullets: [
          "не використовуйте пошкоджені банки",
          "не наливайте окріп у крижане скло",
          "не торкайтеся внутрішньої частини банки руками після стерилізації",
        ],
      },
      {
        title: "Коли банки вже можна використовувати",
        paragraphs: [
          "Стерильні банки бажано наповнювати одразу після підготовки або тримати їх чистими й накритими до моменту використання.",
          "Якщо ви консервуєте гарячі овочі, маринади, компоти чи варення, особливо важливо, щоб і банки, і продукт були підготовлені послідовно та без зайвих пауз.",
        ],
      },
    ],
    relatedRecipes: [
      {
        title: "Мариновані огірки на зиму — класичний рецепт",
        href: "/recipes/marinovani-ogirky-na-zymu-klasychni",
      },
      {
        title: "Солодкі помідори на зиму в маринаді",
        href: "/recipes/solodki-pomidory-na-zymu",
      },
      {
        title: "Яблучний компот на зиму",
        href: "/recipes/yabluchnyi-kompot-na-zymu",
      },
    ],
    relatedGuides: [
      {
        title: "Чому мутніє розсіл",
        href: "/guides/chomu-mutniye-rozsil",
      },
      {
        title: "Як зберігати домашні заготовки",
        href: "/guides/yak-zberigaty-zagotovky",
      },
    ],
    relatedCalculators: [
      {
        title: "Калькулятор маринаду",
        href: "/calculators/marinade",
      },
    ],
  },
  {
    slug: "chomu-mutniye-rozsil",
    title: "Чому мутніє розсіл у банках",
    description:
      "Пояснюємо, чому мутніє розсіл у домашніх заготовках, які причини трапляються найчастіше та коли банку краще не вживати.",
    excerpt:
      "Гайд про помутніння розсолу: поширені причини, ознаки небезпеки та способи зменшити ризик псування заготовок.",
    eyebrow: "Поширені помилки",
    readTime: "6 хв читання",
    updatedAt: "2026-03-16",
    category: "troubleshooting",
    intro: [
      "Помутніння розсолу — одна з найчастіших причин тривоги після консервації. Іноді це пов’язано з дрібними технологічними похибками, а іноді — з реальним псуванням продукту.",
      "Головне правило просте: якщо розсіл мутний, є неприємний запах, здута кришка або інші підозрілі ознаки, таку заготовку краще не вживати.",
    ],
    sections: [
      {
        title: "Через що розсіл мутніє найчастіше",
        paragraphs: [
          "Причиною може бути недостатня чистота банок, кришок або самих овочів. Навіть невеликі залишки бруду чи мікроорганізмів можуть вплинути на прозорість і подальше зберігання.",
          "Ще один фактор — порушення пропорцій солі, цукру чи оцту. Якщо маринад приготовано неточно, заготовка може поводитися нестабільно.",
        ],
        bullets: [
          "погано підготовлені банки чи кришки",
          "нечисті овочі, зелень або спеції",
          "помилки в пропорціях маринаду",
          "занадто тепле місце зберігання",
        ],
      },
      {
        title: "Коли заготовку краще не вживати",
        paragraphs: [
          "Найнебезпечніші ознаки — різкий неприємний запах, газоутворення, піна, здута кришка або витікання рідини. У такому випадку продукт краще одразу утилізувати.",
          "Якщо розсіл просто став менш прозорим, але запах нормальний, а кришка не деформована, це все одно привід уважно оцінити банку, а не вживати її автоматично.",
        ],
      },
      {
        title: "Як зменшити ризик помутніння",
        paragraphs: [
          "Використовуйте лише чисті стерильні банки, ретельно мийте овочі, зелень і спеції, а також дотримуйтеся перевірених рецептів із точними пропорціями.",
          "Після закрутки зберігайте заготовки в темному прохолодному місці без різких перепадів температур.",
        ],
        bullets: [
          "дотримуйтеся перевіреного рецепта",
          "не скорочуйте час підготовки",
          "зберігайте банки в прохолоді",
        ],
      },
    ],
    relatedRecipes: [
      {
        title: "Мариновані огірки на зиму — класичний рецепт",
        href: "/recipes/marinovani-ogirky-na-zymu-klasychni",
      },
      {
        title: "Солодкі помідори на зиму в маринаді",
        href: "/recipes/solodki-pomidory-na-zymu",
      },
    ],
    relatedGuides: [
      {
        title: "Як стерилізувати банки для консервації",
        href: "/guides/sterilizaciya-banok",
      },
      {
        title: "Як зберігати домашні заготовки",
        href: "/guides/yak-zberigaty-zagotovky",
      },
    ],
    relatedCalculators: [
      {
        title: "Калькулятор маринаду",
        href: "/calculators/marinade",
      },
      {
        title: "Конвертер оцту",
        href: "/calculators/vinegar-converter",
      },
    ],
  },
  {
    slug: "fermentaciya-dlya-pochatkivciv",
    title: "Ферментація для початківців",
    description:
      "Простий гайд для тих, хто хоче почати домашню ферментацію: сіль, температура, базові правила та безпечний старт.",
    excerpt:
      "Пояснюємо, як почати ферментацію вдома без зайвого страху: що важливо знати про сіль, температуру та зберігання.",
    eyebrow: "Ферментація",
    readTime: "7 хв читання",
    updatedAt: "2026-03-16",
    category: "fermentation",
    intro: [
      "Ферментація — це природний процес, який дозволяє отримати яскравий смак без оцту й складних інгредієнтів. Саме тому квашені овочі залишаються однією з найулюбленіших домашніх заготовок.",
      "Для хорошого результату не потрібне складне обладнання. Набагато важливіше дотримуватися базових правил: чистота, правильна кількість солі, відповідна температура й уважність у перші дні.",
    ],
    sections: [
      {
        title: "З чого почати",
        paragraphs: [
          "Найзручніше починати з простих продуктів: капусти, моркви, огірків або інших овочів, які добре підходять для домашнього квашення.",
          "Перший рецепт краще обрати без зайвих експериментів, щоб зрозуміти, як поводиться продукт і як змінюється смак у процесі ферментації.",
        ],
      },
      {
        title: "Чому важлива сіль",
        paragraphs: [
          "Сіль допомагає створити середовище, у якому корисні процеси проходять стабільніше, а небажані — пригнічуються. Саме тому у ферментації так важлива точність.",
          "Найкраще не міряти сіль навмання, а орієнтуватися на перевірені пропорції або вагу.",
        ],
        bullets: [
          "не зменшуйте кількість солі випадково",
          "користуйтеся перевіреним рецептом",
          "краще зважувати, ніж міряти на око",
        ],
      },
      {
        title: "Температура і перші дні",
        paragraphs: [
          "На старті ферментація часто проходить при кімнатній температурі, але надмірне тепло може прискорювати процес занадто сильно. Після досягнення потрібного смаку продукт зазвичай переносять у холод.",
          "У перші дні важливо слідкувати за станом овочів, рівнем розсолу та чистотою ємності.",
        ],
      },
      {
        title: "Що допомагає уникнути помилок",
        paragraphs: [
          "Найкраща стратегія для початку — простий рецепт, чиста тара, точні пропорції та спокійне спостереження за процесом без поспіху.",
          "Коли базовий рецепт вдався кілька разів, тоді вже можна експериментувати з додатковими спеціями та овочами.",
        ],
      },
    ],
    relatedRecipes: [
      {
        title: "Квашена капуста — класичний домашній варіант",
        href: "/recipes/kvashena-kapusta-klasychna",
      },
    ],
    relatedGuides: [
      {
        title: "Як зберігати домашні заготовки",
        href: "/guides/yak-zberigaty-zagotovky",
      },
    ],
    relatedCalculators: [
      {
        title: "Калькулятор солі та цукру",
        href: "/calculators/salt-sugar",
      },
    ],
  },
  {
    slug: "yak-zberigaty-zagotovky",
    title: "Як зберігати домашні заготовки",
    description:
      "Поради щодо зберігання домашніх заготовок: де тримати банки, яких умов уникати та на що звертати увагу після закрутки.",
    excerpt:
      "Гайд про зберігання заготовок у домашніх умовах: температура, світло, вологість і ознаки, які не можна ігнорувати.",
    eyebrow: "Зберігання",
    readTime: "5 хв читання",
    updatedAt: "2026-03-16",
    category: "storage",
    intro: [
      "Навіть вдалий рецепт може зіпсуватися, якщо банки зберігаються в невідповідних умовах. Тому після закрутки важливо не лише дочекатися охолодження, а й правильно організувати місце для зберігання.",
      "Ідеально, коли заготовки стоять у прохолодному темному місці без надмірної вологи та різких перепадів температур.",
    ],
    sections: [
      {
        title: "Найкращі умови для зберігання",
        paragraphs: [
          "Домашні заготовки зазвичай краще почуваються в прохолоді й без прямого сонячного світла. Надмірне тепло або різкі перепади температур можуть впливати на смак, колір і стабільність продукту.",
          "Бажано, щоб банки стояли спокійно, не піддавалися ударам і не зберігалися біля джерел тепла.",
        ],
      },
      {
        title: "Що перевіряти після закрутки",
        paragraphs: [
          "Після повного охолодження огляньте банки: кришка має бути щільною, без деформацій, а рідина — без стороннього запаху, піни чи витікання.",
          "Час від часу корисно оглядати заготовки під час зберігання, особливо в перші тижні після приготування.",
        ],
        bullets: [
          "щільність кришки",
          "відсутність піни та витікання",
          "нормальний запах і зовнішній вигляд",
        ],
      },
      {
        title: "Коли банку краще не відкривати",
        paragraphs: [
          "Якщо кришка здулася, рідина витекла, з’явився різкий неприємний запах або є інші підозрілі зміни, таку заготовку краще не вживати.",
          "Обережність у таких випадках важливіша за бажання врятувати продукт.",
        ],
      },
    ],
    relatedRecipes: [
      {
        title: "Яблучний компот на зиму",
        href: "/recipes/yabluchnyi-kompot-na-zymu",
      },
      {
        title: "Полуничне варення-п’ятихвилинка",
        href: "/recipes/polunyczne-varennya-pyatykhvylynka",
      },
    ],
    relatedGuides: [
      {
        title: "Як стерилізувати банки для консервації",
        href: "/guides/sterilizaciya-banok",
      },
      {
        title: "Чому мутніє розсіл",
        href: "/guides/chomu-mutniye-rozsil",
      },
    ],
    relatedCalculators: [
      {
        title: "Калькулятори пропорцій",
        href: "/calculators",
      },
    ],
  },
]

export function getAllGuides() {
  return guides
}

export function getGuideBySlug(slug: string) {
  return guides.find((guide) => guide.slug === slug)
}

export function getGuideSlugs() {
  return guides.map((guide) => guide.slug)
}